import { createLink, type LinkComponent } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import styles from './Button.module.css';
import { cx } from './cx';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends Omit<AriaButtonProps, 'className' | 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
  className?: string;
  children?: ReactNode;
  /** Sets data-testid (acceptance tests address controls by it). */
  testId?: string;
}

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md'): string {
  return cx(styles.button, styles[variant], size === 'sm' && styles.sm);
}

/** Button with the design-system variants. `isPending` shows a spinner and keeps focus. */
export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  className,
  children,
  isPending,
  testId,
  ...props
}: ButtonProps) {
  return (
    <AriaButton
      {...props}
      {...(testId ? { 'data-testid': testId } : {})}
      isPending={isPending ?? false}
      className={cx(buttonClass(variant, size), className)}
    >
      {isPending ? <LoaderCircle className={styles.spinner} size={16} aria-hidden="true" /> : icon}
      {children}
    </AriaButton>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon'> {
  /** Accessible name; also shown as a tooltip. */
  label: string;
  icon: ReactNode;
}

export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'md',
  className,
  ...props
}: IconButtonProps) {
  return (
    <Button
      {...props}
      variant={variant}
      size={size}
      aria-label={label}
      className={cx(styles.iconButton, className)}
    >
      <span aria-hidden="true" title={label} style={{ display: 'inline-flex' }}>
        {icon}
      </span>
    </Button>
  );
}

interface StyledAnchorProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: ReactNode;
}

const StyledAnchor = forwardRef<HTMLAnchorElement, StyledAnchorProps>(function StyledAnchor(
  { variant = 'secondary', size = 'md', icon, className, children, ...props },
  ref,
) {
  return (
    <a ref={ref} {...props} className={cx(buttonClass(variant, size), styles.link, className)}>
      {icon}
      {children}
    </a>
  );
});

/** A router link that looks like a button (navigation keeps link semantics). */
export const ButtonLink: LinkComponent<typeof StyledAnchor> = createLink(StyledAnchor);
